import React, { useState, useEffect, useRef } from 'react';
import { Tooltip, Button, Dialog, Classes, Intent, InputGroup } from "@blueprintjs/core";
import Endpoints from "../../../endpoints.js";

function groupBy(list, keyGetter) {
    const map = new Map();
    list.forEach((item) => {
         const key = keyGetter(item);
         const collection = map.get(key);
         if (!collection) {
             map.set(key, [item]);
         } else {
             collection.push(item);
         }
    });
    return map;
}

function MessageTemplateDialog(props) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dynamicParams, setDynamicParams] = useState({});
  const [preview, setPreview] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(props.defaultValue.telefonoCelular);
  const [image, setImage] = useState('');
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTemplates, setFilteredTemplates] = useState(props.templateList);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredTemplate, setHoveredTemplate] = useState(null); // Estado para la plantilla sobre la que se pasa el mouse
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (props.isOpen) {
      setSelectedTemplate(null);
      setLoading(false);
      setDynamicParams({});
      setPreview('');
      setPhoneNumber(props.defaultValue.telefono_celular);
      setImage('');
      setError('');
      setSearchTerm('');
      setFilteredTemplates(props.templateList);
      setIsDropdownOpen(false);
      setHoveredTemplate(null);
    }
  }, [props.isOpen, props.defaultValue.telefono_celular, props.templateList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setHoveredTemplate(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    document.getElementById('div-status').innerHTML = '';
    setError('');

    // const recipient = props.defaultValue.hasOwnProperty("telefono_celular") ? props.defaultValue.telefono_celular : phoneNumber;
    const recipient = props.defaultValue.hasOwnProperty("telefonoCelular") ? props.defaultValue.telefonoCelular : phoneNumber;

    setLoading(true);

    const shouldIncludeHeader = selectedTemplate?.header?.type === "image";

    const header = {
      type: "image",
      image: {
        link: image || null
      }
    };

    const name = props.defaultValue.hasOwnProperty("nombre_completo") ? props.defaultValue.nombre_completo.split(' ').map(w => w[0].toUpperCase() + w.substr(1).toLowerCase()).join(' ') : '(Sin nombre)';
    const body = JSON.stringify({
      action: selectedTemplate.id,
      payload: {
        recipient,
        recipient_name: name,
        params: {
          body: dynamicParams,
          header: shouldIncludeHeader ? header : null
        }
      },
      agent_email: props.currentAgent.usuario.emailreal,
      agent_group: props.currentAgent.usuario.group,
      template_text: selectedTemplate.text
    });

    try {
      const response = await fetch(Endpoints().GCP_SEND_TEMPLATE_URL, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${props.currentAgent.stsTokenManager.accessToken}`
        },
        body
      });

      if (response.ok) {
        setPreview('');
        setDynamicParams({});
        setImage('');
        props.close();
      } else {
        handleErrorResponse(response.status, recipient);
      }
    } catch (error) {
      setError('Ocurrió un error al enviar la solicitud. Por favor, intenta nuevamente.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleErrorResponse = (status, recipient) => {
    let lblResponse = '';
    if (status === 403) {
      lblResponse = 'Contacto bloqueado REUS';
    } else if (status === 404) {
      lblResponse = 'Contacto no encontrado en Whatsapp';
    } else if (status === 400) {
      lblResponse = 'Error al enviar plantilla';
    } else {
      lblResponse = 'Error desconocido';
    }
    document.getElementById('div-status').innerHTML = `No se pudo enviar mensaje al número <b>${recipient}</b> ${lblResponse}.`;
    document.getElementById('txtNumero').focus();
  };

  const handleSearchChange = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    setIsDropdownOpen(true);

    const filtered = props.templateList.filter(template =>
      template.id.toLowerCase().includes(term) ||
      template.text.toLowerCase().includes(term)
    );
    setFilteredTemplates(filtered);
};

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setSearchTerm(template.id);
    setIsDropdownOpen(false);
    setHoveredTemplate(null);

    const previewValue = template.text
      .replace(/~([^~]*)~/g, '<del>$1</del>')
      .replace(/_([^_]*)_/g, '<em>$1</em>')
      .replace(/\*([^*]*)\*/g, '<strong>$1</strong>')
      .replace(/```(.*)```/g, '<code>$1</code>')
      .replace(/\n/g, '<br/>');

    setPreview(previewValue);
    setDynamicParams({});
    setImage('');
  };

  const formatTemplateText = (text) => {
    return text
      .replace(/~([^~]*)~/g, '<del>$1</del>')
      .replace(/_([^_]*)_/g, '<em>$1</em>')
      .replace(/\*([^*]*)\*/g, '<strong>$1</strong>')
      .replace(/```(.*)```/g, '<code>$1</code>')
      .replace(/\n/g, '<br/>');
  };

  const handleMouseEnter = (template) => {
    setHoveredTemplate(template);
  };

  const handleMouseLeave = () => {
    setHoveredTemplate(null);
  };

  const handleChangeImage = (e) => {
    setImage(e.target.value);
  };

  const handleChangeOnDynamic = (data, event) => {
    const index = data + 1;

    const tmp = {
      [index]: {
        Type: "text",
        Text: event.target.value,
        Order: index
      }
    };

    const newState = { ...dynamicParams, ...tmp };

    const previewValue = selectedTemplate.text
      .replace(`{{1}}`, newState[1]?.Text || `{{1}}`)
      .replace(`{{2}}`, newState[2]?.Text || `{{2}}`)
      .replace(`{{3}}`, newState[3]?.Text || `{{3}}`)
      .replace(`{{4}}`, newState[4]?.Text || `{{4}}`)
      .replace(`{{5}}`, newState[5]?.Text || `{{5}}`);

    setPreview(previewValue);
    setDynamicParams(newState);
  };

  const handlePhoneNumber = (e) => setPhoneNumber(e.target.value);

  const renderImage = () => {
    if (!selectedTemplate?.id || selectedTemplate?.header?.type !== "image") {
      return null;
    }

    return (
      <div className="row align-items-center">
        <div className="col">
          <div className="input-group mb-3 input-group-sm">
            <select onChange={handleChangeImage} name="cmbImage" className="form-control form-control-sm" required>
              <option defaultValue value="">Selecciona la imagen</option>
              {renderOptions()}
            </select>
          </div>
        </div>
      </div>
    );
  };

  const renderOptions = () => {
    const imageReplies = (props.quickReplies || []).filter((x) => x.ReplyType.includes("image"))
    const grouped = Array.from(groupBy(imageReplies, x => x.CategoryName.String)).sort((a, b) => a[0].localeCompare(b[0]));

    return grouped.map(([group, items]) => (
      <optgroup label={group} key={group}>
        {items.sort((a, b) => a.FileName.String.localeCompare(b.FileName.String)).map(x => (
          <option value={x.ReplyLink} key={x.Id}>{x.FileName.String}</option>
        ))}
      </optgroup>
    ));
  };

  const renderDynamicFields = () => {
    if (!selectedTemplate?.id) {
      return null;
    }

    return selectedTemplate.exportFields.split(",").splice(1).map((f, index) => (
      <div className="row align-items-center" key={`${selectedTemplate.id}${f}`}>
        <div className="col">
          <div className="input-group mb-3 input-group-sm">
            <input
              onChange={handleChangeOnDynamic.bind(this, index)}
              required
              type="text"
              className="form-control"
              placeholder={f} />
          </div>
        </div>
      </div>
    ));
  };

  const checkAttentionSchedule = (daysArray) => {
    if (!daysArray || daysArray.length === 0) {
      return true;
    }

    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.getHours() * 100 + now.getMinutes();
    
    const todaySchedule = daysArray.find(schedule => schedule.id === currentDay);

    if (!todaySchedule) {
      return false;
    }
    
    const isWithinSchedule = currentTime >= todaySchedule.startTime && 
                            currentTime <= todaySchedule.endTime;

    return isWithinSchedule;
  };

  return (
    <>
      {!checkAttentionSchedule(props.attentionDays) ? (
        <Dialog onClose={props.close} isOpen={props.isOpen}>
          <div className={Classes.DIALOG_BODY}>
            <label className="label">
              <strong>{props.parameter?.EXECUTIVE_OUTOFSCHEDULE_PROMPT}</strong>
            </label>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button onClick={props.close}>Cerrar</Button>
            </div>
          </div>
        </Dialog>
      ) : (
        <Dialog
          icon="new-person"
          onClose={props.close}
          title="Nuevo mensaje"
          isOpen={props.isOpen}>
          <form onSubmit={handleSubmit}>
            <div className={Classes.DIALOG_BODY}>
              <table className="bp3-html-table" style={{ width: "101%" }}>
                <thead>
                  <tr>
                    <th>Telefono</th>
                    <th>Plantilla</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <Tooltip content="Ingresa el número de teléfono">
                        <InputGroup
                        leftIcon="phone"
                        placeholder="Sólo números sin espacio"
                        small={false}
                        required
                        defaultValue={props.defaultValue.telefonoCelular}
                        disabled={props.defaultValue.hasOwnProperty("telefonoCelular")}
                        onChange={handlePhoneNumber}
                        id='txtNumero' />
                      </Tooltip>
                    </td>
                    <td style={{ position: 'relative' }}>
                      {/* <Tooltip content="Busca y selecciona la plantilla"> */}
                        <InputGroup
                          placeholder="Busca una plantilla"
                          value={searchTerm}
                          onChange={handleSearchChange}
                          onFocus={() => setIsDropdownOpen(true)}
                          small={false}
                          required
                        />
                      {/* </Tooltip> */}
                      {isDropdownOpen && (
                        <div
                          ref={dropdownRef}
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            maxHeight: '200px',
                            background: '#fff',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            zIndex: 1000,
                            display: 'flex',
                            width: '400px' // Aumentar el ancho para dar espacio a las dos columnas
                          }}
                        >
                          {/* Columna izquierda: Lista de plantillas */}
                          <div
                            style={{
                              flex: 1,
                              maxHeight: '200px',
                              overflowY: 'auto',
                              borderRight: '1px solid #ccc'
                            }}
                          >
                            {filteredTemplates.length > 0 ? (
                              filteredTemplates.map(template => (
                                <div
                                  key={template.id}
                                  onClick={() => handleTemplateSelect(template)}
                                  onMouseEnter={() => handleMouseEnter(template)}
                                  onMouseLeave={handleMouseLeave}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    background: selectedTemplate?.id === template.id ? '#e6f0ff' : hoveredTemplate?.id === template.id ? '#f0f0f0' : '#fff',
                                    borderBottom: '1px solid #eee',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {template.id}
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '8px 12px', color: '#888' }}>
                                No se encontraron plantillas
                              </div>
                            )}
                          </div>
                          {/* Columna derecha: Texto de la plantilla al pasar el mouse */}
                          <div
                            style={{
                              flex: 1,
                              maxHeight: '200px',
                              overflowY: 'auto',
                              padding: '8px',
                              background: '#f9f9f9',
                              display: 'flex',
                              alignItems: hoveredTemplate ? 'flex-start' : 'center',
                              justifyContent: hoveredTemplate ? 'flex-start' : 'center'
                            }}
                          >
                            {hoveredTemplate ? (
                              <div
                                dangerouslySetInnerHTML={{ __html: formatTemplateText(hoveredTemplate.text) }}
                                style={{ fontSize: '14px', color: '#333' }}
                              />
                            ) : (
                              <div style={{ color: '#888', fontStyle: 'italic' }}>
                                Pasa el mouse sobre una plantilla para ver su contenido
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="column">
                <label className="label"><strong>Parámetros:</strong></label>
              </div>

              {renderImage()}
              {renderDynamicFields()}

              <div className="column">
                <label className="label"><strong>Vista previa</strong></label>
                <div className="control">
                  <div className="content" dangerouslySetInnerHTML={{ __html: preview }} style={{ height: "120px", border: "rgb(170, 170, 170) 1px dotted", padding: "10px", fontStyle: "italic", background: "white" }}></div>
                </div>
                <div className="control">
                  <div id="div-status" className="content" style={{ height: '120px', color: 'red' }}>
                    {error}
                  </div>
                </div>
              </div>
            </div>

            <div className={Classes.DIALOG_FOOTER}>
              <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                <Tooltip content="Cierra el recuadro sin enviar">
                  <Button onClick={props.close}>Cancelar</Button>
                </Tooltip>
                <Tooltip content="Enviar mensaje">
                  <Button type="submit" intent={Intent.PRIMARY} loading={loading}>Enviar</Button>
                </Tooltip>
              </div>
            </div>
          </form>
        </Dialog>
      )}
    </>
  );
}

export default MessageTemplateDialog;